/**
 * 역발행 엑셀 파서 — 레거시 `reverseTaxUploadXLSXHeaderMap` 기준
 */
import * as XLSX from 'xlsx';
import type { TaxInvoiceUpsertRequest } from '@/entities/tax-invoice/api/tax-invoice.api';

const UPLOAD_HEADER_ALIASES: Record<keyof TaxInvoiceUpsertRequest, string[]> = {
  settlementYearMonth: ['정산년월', 'settlement_year_month', 'settlementYearMonth'],
  writeDate: ['작성일자', 'write_date', 'writeDate'],
  email: ['사용자계정', '사용자 계정', 'email', '이메일'],
  businessNumber: ['사업자번호', 'business_number', 'businessNumber'],
  // 화면 표기는 업체명으로 통일했지만, 예전 양식(회사명) 파일도 계속 받는다
  companyName: ['업체명', '회사명', 'company_name', 'companyName'],
  ceoName: ['대표자명', 'ceo_name', 'ceoName'],
  taxInvoiceEmail: [
    '세금계산서이메일',
    '세금계산서 이메일',
    'tax_invoice_email',
    'taxInvoiceEmail',
  ],
  fixedAmount: ['확정금액', '발행총액', 'fixed_amount', 'fixedAmount'],
  itemName: ['품목', 'item_name', 'itemName'],
  supplyCost: ['공급가액', 'supply_cost', 'supplyCost'],
  taxAmount: ['세액', 'tax_amount', 'taxAmount'],
};

const normalizeHeader = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, '')
    .toLowerCase();

const findColumnIndex = (headers: string[], aliases: string[]): number => {
  const normalized = headers.map(normalizeHeader);
  for (const alias of aliases) {
    const idx = normalized.indexOf(normalizeHeader(alias));
    if (idx >= 0) return idx;
  }
  return -1;
};

const parseNumber = (value: unknown): number | undefined => {
  if (value == null || value === '') return undefined;
  const num = Number(String(value).replace(/,/g, ''));
  return Number.isNaN(num) ? undefined : num;
};

const normalizeYm = (value: unknown): string => {
  const raw = String(value ?? '').replace(/\D/g, '');
  if (raw.length === 6) return raw;
  if (raw.length === 8) return raw.slice(0, 6);
  return '';
};

const normalizeDate = (value: unknown): string => {
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  const raw = String(value ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  return raw;
};

export const parseReverseTaxExcel = async (
  file: File,
): Promise<TaxInvoiceUpsertRequest[]> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const sheetRows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: null,
    blankrows: false,
  });

  if (sheetRows.length < 2) return [];

  const headers = (sheetRows[0] ?? []).map((cell) => String(cell ?? ''));
  const columnMap = Object.fromEntries(
    Object.entries(UPLOAD_HEADER_ALIASES).map(([key, aliases]) => [
      key,
      findColumnIndex(headers, aliases),
    ]),
  ) as Record<keyof TaxInvoiceUpsertRequest, number>;

  if (columnMap.email < 0) {
    throw new Error('엑셀에 "사용자 계정" 열을 찾을 수 없습니다.');
  }

  const items: TaxInvoiceUpsertRequest[] = [];
  for (let i = 1; i < sheetRows.length; i++) {
    const row = sheetRows[i] ?? [];
    const email = String(row[columnMap.email] ?? '').trim();
    if (!email) continue;

    const settlementYearMonth =
      columnMap.settlementYearMonth >= 0
        ? normalizeYm(row[columnMap.settlementYearMonth])
        : '';
    const writeDate =
      columnMap.writeDate >= 0
        ? normalizeDate(row[columnMap.writeDate])
        : new Date().toISOString().slice(0, 10);

    const fixedAmount = parseNumber(row[columnMap.fixedAmount]);
    const supplyCost = parseNumber(row[columnMap.supplyCost]);
    const taxAmount = parseNumber(row[columnMap.taxAmount]);

    items.push({
      settlementYearMonth,
      writeDate,
      email,
      businessNumber:
        columnMap.businessNumber >= 0
          ? String(row[columnMap.businessNumber] ?? '').trim()
          : undefined,
      companyName:
        columnMap.companyName >= 0
          ? String(row[columnMap.companyName] ?? '').trim()
          : undefined,
      ceoName:
        columnMap.ceoName >= 0
          ? String(row[columnMap.ceoName] ?? '').trim()
          : undefined,
      taxInvoiceEmail:
        columnMap.taxInvoiceEmail >= 0
          ? String(row[columnMap.taxInvoiceEmail] ?? '').trim()
          : undefined,
      itemName:
        columnMap.itemName >= 0
          ? String(row[columnMap.itemName] ?? '').trim()
          : undefined,
      fixedAmount,
      supplyCost,
      taxAmount,
    });
  }

  return items;
};
